import { api } from "@/lib/api";
import { cn, relativeTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
	ArrowDownLeft,
	ArrowUpRight,
	Bot,
	Clock,
	Loader2,
	Mail,
	MessageSquare,
} from "lucide-react";

// ============================================================
// Booking Detail — Activity Tab (Unified Feed)
// ============================================================

interface ActivityEntry {
	id: string;
	type: "history" | "email" | "chat";
	createdAt: string | null;
	// history fields
	field?: string;
	oldValue?: string | null;
	newValue?: string | null;
	source?: string;
	// email fields
	subject?: string | null;
	fromEmail?: string | null;
	direction?: string | null;
	classified?: string | null;
	// chat fields
	role?: string | null;
	content?: string | null;
	action?: string | null;
}

export function ActivityTab({ bookingId }: { bookingId: string }) {
	const query = useQuery({
		queryKey: ["booking-activity", bookingId],
		queryFn: () =>
			api.get<{ data: ActivityEntry[] }>(`/bookings/${bookingId}/activity`),
	});

	const items = query.data?.data ?? [];

	if (query.isLoading) {
		return (
			<div className="flex justify-center py-8">
				<Loader2 className="h-6 w-6 animate-spin text-stone-400" />
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div className="text-center py-12 text-stone-400">
				Noch keine Aktivität.
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{items.map((item) => (
				<ActivityItem key={`${item.type}-${item.id}`} item={item} />
			))}
		</div>
	);
}

function ActivityItem({ item }: { item: ActivityEntry }) {
	switch (item.type) {
		case "history":
			return <HistoryActivity item={item} />;
		case "email":
			return <EmailActivity item={item} />;
		case "chat":
			return <ChatActivity item={item} />;
		default:
			return null;
	}
}

function HistoryActivity({ item }: { item: ActivityEntry }) {
	const isNote = item.field === "note";
	return (
		<div className="card flex items-start gap-3 !py-3">
			{isNote ? (
				<MessageSquare size={14} className="text-amber-500 mt-0.5 shrink-0" />
			) : (
				<Clock size={14} className="text-stone-400 mt-0.5 shrink-0" />
			)}
			<div className="flex-1 min-w-0">
				{isNote ? (
					<p className="text-sm text-stone-800 whitespace-pre-wrap">
						{item.newValue}
					</p>
				) : (
					<p className="text-sm">
						<span className="font-medium text-stone-700">{item.field}</span>
						{item.oldValue && (
							<span className="text-stone-400">
								{" "}
								von &quot;{item.oldValue}&quot;
							</span>
						)}
						<span className="text-stone-400"> → </span>
						<span className="text-stone-900">&quot;{item.newValue}&quot;</span>
					</p>
				)}
				{item.createdAt && (
					<p className="text-xs text-stone-400 mt-0.5">
						{relativeTime(item.createdAt)} · {item.source ?? "system"}
					</p>
				)}
			</div>
		</div>
	);
}

function EmailActivity({ item }: { item: ActivityEntry }) {
	const isInbound = item.direction === "inbound";
	return (
		<div className="card flex items-start gap-3 !py-3">
			<div
				className={cn(
					"mt-0.5 shrink-0 rounded-full p-1",
					isInbound ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600",
				)}
			>
				{isInbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
			</div>
			<div className="flex-1 min-w-0">
				<p className="text-sm">
					<Mail size={12} className="inline mr-1 text-stone-400" />
					<span className="font-medium text-stone-700">
						{item.subject ?? "(Kein Betreff)"}
					</span>
				</p>
				<div className="flex gap-2 text-xs text-stone-500 mt-0.5">
					<span>
						{isInbound ? "Eingang" : "Ausgang"}: {item.fromEmail ?? "—"}
					</span>
					{item.createdAt && <span>{relativeTime(item.createdAt)}</span>}
				</div>
			</div>
		</div>
	);
}

function ChatActivity({ item }: { item: ActivityEntry }) {
	const isAssistant = item.role === "assistant";
	return (
		<div className="card flex items-start gap-3 !py-3">
			<Bot
				size={14}
				className={cn(
					"mt-0.5 shrink-0",
					isAssistant ? "text-purple-500" : "text-stone-400",
				)}
			/>
			<div className="flex-1 min-w-0">
				<p className="text-sm text-stone-800 whitespace-pre-wrap line-clamp-3">
					{item.content}
				</p>
				<div className="flex gap-2 text-xs text-stone-400 mt-0.5">
					<span>{isAssistant ? "AI" : "User"}</span>
					{item.action && (
						<span className="badge bg-purple-50 text-purple-600 text-xs">
							{item.action}
						</span>
					)}
					{item.createdAt && <span>{relativeTime(item.createdAt)}</span>}
				</div>
			</div>
		</div>
	);
}
